import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  entityAccessReceivedInvitationsChanged, entityAccessSetDeleteReceivedInvitations,
} from '~/store/frontendState/entityAccessInvitations/entityAccessInvitations.actionCreators';
import { mapListingChanged } from '~/store/frontendState/mapListing/mapListing.actionCreators';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import type { ApiError } from '../utils/api/apiError.helpers';
import { useTranslation } from '../utils/hooks';
import { useAjaxCall } from '../utils/hooks/useAjaxCall';
import {
  acceptInvitation, createInvitation, deleteInvitation,
  type EntityAccessInvitationId,
  getCreatedInvitations,
} from './entityAccess.invitations.repository';
import {
  EntityType, type NewEntityAccess,
} from './entityAccess.repository';
import { entityAccessInvitationResponseToEntityAccessInvitation } from './entityAccess.repository.helpers';

const EMPTY_INVITATIONS = [] as const;

export const useManageEntityAccessInvitations = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();

  const { invokeAjax: create, isLoading: isCreatingLoading } = useAjaxCall(createInvitation);
  const { invokeAjax: remove, isLoading: isRevokingLoading } = useAjaxCall(deleteInvitation);
  const { invokeAjax: accept, isLoading: isAcceptingLoading } = useAjaxCall(acceptInvitation);

  const createEntityAccessInvitation = useCallback((newEntityAccesses: ReadonlyArray<NewEntityAccess>) => {
    if (!clientId) {
      return Promise.reject();
    }
    return create(clientId, newEntityAccesses);
  }, [clientId, create]);

  const revokeEntityAccessInvitation = useCallback((invitationId: EntityAccessInvitationId) => {
    if (!clientId) {
      return Promise.reject();
    }

    return remove(clientId, invitationId)
      .catch((e: ApiError) => {
        if (e.responseStatus === 403) {
          dispatch(createAppError({
            type: AppErrorType.General,
            title: t('Error'),
            content: t('accessInvitations.errors.403.text'),
          }));
        }
      });
  }, [clientId, dispatch, remove, t]);

  const rejectEntityAccessInvitation = useCallback((invitationId: EntityAccessInvitationId) => {
    if (!clientId) {
      return Promise.reject();
    }

    dispatch(entityAccessSetDeleteReceivedInvitations(new Set([invitationId])));

    return remove(clientId, invitationId)
      .catch((e: ApiError) => {
        if (e.responseStatus !== 403) { // 403 does not need handling, as rejecting an invalid invitation is not an error
          dispatch(createAppError({
            type: AppErrorType.General,
            title: t('An unknown error has occurred.'),
            content: t('Please try again. If this problem persists, please contact the customer support.'),
          }));
        }
      })
      .finally(() => {
        dispatch(entityAccessReceivedInvitationsChanged());
      });
  }, [clientId, dispatch, remove, t]);

  const acceptEntityAccessInvitation = useCallback((invitationId: EntityAccessInvitationId) => {
    if (!clientId) {
      return Promise.reject();
    }

    dispatch(entityAccessSetDeleteReceivedInvitations(new Set([invitationId])));

    return accept(clientId, invitationId)
      .then(() => {
        dispatch(mapListingChanged());
      })
      .catch((e: ApiError) => {
        if (e.responseStatus === 403) {
          dispatch(createAppError({
            type: AppErrorType.General,
            title: t('Error'),
            content: t('accessInvitations.errors.403.text'),
          }));
        }
      })
      .finally(() => {
        dispatch(entityAccessReceivedInvitationsChanged());
      });
  }, [clientId, dispatch, accept, t]);

  return {
    createEntityAccessInvitation,
    rejectEntityAccessInvitation,
    revokeEntityAccessInvitation,
    acceptEntityAccessInvitation,
    isLoading: isCreatingLoading || isRevokingLoading || isAcceptingLoading,
  };
};

export const useGetEntityAccessInvitationsCreatedByCurrentUser = () => {
  const clientId = useClientIdSelector();

  const getEntityAccessInvitations = useCallback(() => {
    if (clientId) {
      return getCreatedInvitations(clientId);
    }
    else {
      return Promise.resolve({ list: [] as const });
    }
  }, [clientId]);

  const { isLoading, error, data, invokeAjax } = useAjaxCall(getEntityAccessInvitations);

  const processedData = useMemo(() => ({
    list: data?.list.map((item) => entityAccessInvitationResponseToEntityAccessInvitation(item)) || [],
  }), [data]);

  return {
    isLoading,
    error,
    data: processedData,
    invokeAjax,
  };
};

export const useGetMapAccessInvitationsCreatedByCurrentUser = (mapId?: number) => {
  const {
    invokeAjax,
    data,
    isLoading,
  } = useGetEntityAccessInvitationsCreatedByCurrentUser();

  const mapInvitationsData = useMemo(() => {
    if (!data || !mapId) {
      return EMPTY_INVITATIONS;
    }
    return data.list.filter((invitation) =>
      invitation.entityType === EntityType.MAP &&
      invitation.entityId === mapId
    );
  }, [data, mapId]);

  return {
    getMapAccessInvitations: invokeAjax,
    mapInvitationsData,
    isLoading,
  };
};
